'use client';

import { useRouter } from '@bprogress/next/app';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { GTMEventAuth, sendGTMEventAuth } from '@/fo/api/gtm';
import { FeatureAuthFormForgotPassword } from '@/fo/feature/auth/form/forgot-password';
import { FeatureAuthFormLogin } from '@/fo/feature/auth/form/login';
import { FeatureAuthFormRegistration } from '@/fo/feature/auth/form/registration';
import { UiAuthModalBase } from '@/fo/ui/auth/modal/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';

export interface FeatureAuthModalBaseProps {
  isAuthorized?: boolean;
  agreementSales: string;
  agreementPrivacy: string;
}

export function FeatureAuthModalBase({
  isAuthorized,
  agreementSales,
  agreementPrivacy
}: FeatureAuthModalBaseProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const refReferral = useRef<GTMEventAuth['referral']>('header');
  const refAuthorized = useRef(isAuthorized);
  const tabRef = useRef<'login' | 'registration'>('login');

  useEffect(() => {
    const authParam = searchParams.get('auth');
    if (!authParam) return;

    if (!isAuthorized) {
      if (authParam === 'open') {
        utilEventAuthModal.open('header');
      }

      if (authParam === 'forgot-password') {
        utilEventAuthModal.open('header');
        utilEventAuthModal.showForgotPassword();
      }
    }

    const params = new URLSearchParams(searchParams);
    params.delete('auth');
    const query = params.toString();
    router.replace(`${window.location.pathname}${query ? `?${query}` : ''}`, {
      scroll: false
    });
  }, [isAuthorized, searchParams, router]);

  useEffect(() => {
    if (isAuthorized) return;

    const unsubscribeOnOpen = utilEventAuthModal.onOpen((referral) => {
      refReferral.current = referral;
    });
    return () => {
      unsubscribeOnOpen();
    };
  }, [isAuthorized]);

  useEffect(() => {
    if (isAuthorized && refAuthorized.current !== isAuthorized) {
      const targetUrl = utilEventAuthModal.getRedirectUrl();

      if (targetUrl) {
        window.location.assign(targetUrl);
        return;
      }

      sendGTMEventAuth({
        event: tabRef.current === 'login' ? 'authorization' : 'registration',
        referral: refReferral.current
      });
      refReferral.current = 'header';
      tabRef.current = 'login';
    }
    refAuthorized.current = isAuthorized;
  }, [isAuthorized]);

  if (isAuthorized) {
    return null;
  }

  return (
    <UiAuthModalBase
      loginForm={<FeatureAuthFormLogin />}
      registrationForm={
        <FeatureAuthFormRegistration
          agreementSales={agreementSales}
          agreementPrivacy={agreementPrivacy}
        />
      }
      forgotPasswordForm={<FeatureAuthFormForgotPassword />}
      onTabChange={(tabName) => (tabRef.current = tabName)}
    />
  );
}
